/**
 * Canonical Crewlink timezone (D-009, PT §15, INV-T17).
 *
 * ONE timezone, stored as an IANA identifier on the profile. Seeded from the
 * device ONCE and never automatically changed afterwards — not by travel, not
 * by a device timezone change, not by opening the app on another machine.
 *
 * "Seeded once" is enforced in the DATABASE, by `ensure_app_user` (migration
 * 0001), which ignores the supplied timezone when a profile row already
 * exists. The functions here only READ the device zone in order to offer it
 * the first time. Nothing in this file can overwrite a stored value.
 *
 * DISPLAY RULE: past events are absolute UTC instants and are never rewritten;
 * they render in the canonical timezone. No code path may read the device
 * timezone to interpret or display a stored instant — that is INV-T17, and it
 * is why `readDeviceTimeZone` is named for what it is rather than being a
 * general-purpose helper.
 */

/**
 * Fallback used by `readDeviceTimeZone` ONLY — the profile-seeding path.
 *
 * A seeded profile default is a visible setting Jerry can inspect and change,
 * so a UTC fallback there is a usable starting point. Capture creation must
 * NEVER receive this substitute: a Capture's `created_timezone` is permanent
 * provenance (D-016), and `readCaptureTimeZone` below returns `null` instead.
 */
const FALLBACK_TIME_ZONE = 'UTC';

/**
 * Same coarse shape check as the database constraints.
 *
 * **WIDENED IN M3 / S-03-02 TO MATCH MIGRATION 0005.** It previously required
 * at least one `/`, mirroring migration 0001 — but 0005 (R1 finding F1)
 * corrected both database constraints to accept single-segment identifiers,
 * and this copy was left behind.
 *
 * The drift was not cosmetic. `readDeviceTimeZone` falls back to `UTC` whenever
 * this check fails, so a device genuinely reporting `EST`, `GMT` or `HST` would
 * have had its zone **silently replaced with `UTC`** — inventing provenance,
 * which is precisely what D-016 forbids. Every Capture from such a device would
 * have carried a timezone the device never reported.
 *
 * Rejects blanks and obvious junk; does not attempt to validate against the
 * IANA database, which changes with every tzdata release.
 */
const IANA_SHAPE = /^[A-Za-z][A-Za-z0-9+_-]*(\/[A-Za-z0-9+_.-]+)*$/;

export function isPlausibleIanaTimeZone(value: string | null | undefined): boolean {
  if (!value) return false;
  return IANA_SHAPE.test(value.trim());
}

/**
 * The device's current IANA timezone, for PROFILE SEEDING ONLY.
 *
 * Never call this to render a timestamp or to decide what "today" means — read
 * the canonical value from the profile instead.
 *
 * **THIS FUNCTION SUBSTITUTES `UTC` when the runtime reports nothing usable**,
 * which is acceptable only because its one caller offers a profile default the
 * server ignores after first seeding. It must never supply a Capture's
 * `created_timezone` — that is `readCaptureTimeZone`, which refuses instead of
 * substituting (S-03-02 R1 finding F-2).
 */
export function readDeviceTimeZone(): string {
  try {
    const resolved = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return isPlausibleIanaTimeZone(resolved) ? resolved.trim() : FALLBACK_TIME_ZONE;
  } catch {
    return FALLBACK_TIME_ZONE;
  }
}

/**
 * The device zone for CAPTURE CREATION — strict, and it never invents one.
 *
 * A Capture's `created_timezone` is permanent provenance (D-016): it records
 * what the device actually reported at the moment of creation. A `UTC` that
 * the runtime never reported is a fabrication, and once stored it is
 * indistinguishable from a device that genuinely reported `UTC`.
 *
 * So: a reported zone that passes the 0005-aligned shape check is returned
 * exactly as reported — `America/New_York`, a genuine `UTC`, a genuine `EST`.
 * A missing, blank or malformed value, or a throwing `Intl` lookup, returns
 * `null`, and the Capture screen surfaces a controlled save error instead of
 * proceeding (S-03-02 R1 finding F-2). Nothing left the device at that point,
 * so the draft stays fully editable and a later tap simply reads again.
 */
export function readCaptureTimeZone(): string | null {
  try {
    const resolved = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return isPlausibleIanaTimeZone(resolved) ? resolved.trim() : null;
  } catch {
    return null;
  }
}

/**
 * Format an absolute instant in the CANONICAL Crewlink timezone.
 *
 * The timezone is a required argument on purpose: there is no default that
 * would quietly fall back to the device, which is exactly the mistake INV-T17
 * exists to prevent.
 */
export function formatInstant(
  instant: Date | string,
  canonicalTimeZone: string,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' },
): string {
  const date = typeof instant === 'string' ? new Date(instant) : instant;
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, {
    ...options,
    timeZone: canonicalTimeZone,
  }).format(date);
}

// ===========================================================================
// CHOOSING a timezone — M12 / S12-00
// ===========================================================================

/**
 * **THE ACCEPTANCE CATEGORY — the rule the picker and the server both apply.**
 *
 * The database is the FINAL authority (its own tzdata decides what exists), but
 * a picker that offered values the server is known to refuse would be offering
 * a dead end. So this encodes the same CATEGORY rules `set_app_user_timezone`
 * applies, and nothing more:
 *
 *   * the shape the profile CHECK requires;
 *   * **not** a `posix/…` or `right/…` duplicate tree;
 *   * a `Region/City` identifier, or exactly `UTC`.
 *
 * That last rule is what excludes abbreviation-style legacy zones such as `EST`
 * and `EST5EDT` — which really are in tzdata, and which have no daylight-saving
 * rule, so storing one would quietly put half the year an hour out.
 *
 * **IT DELIBERATELY DOES NOT SECOND-GUESS EXISTENCE.** Crewlink keeps no list
 * of place names; whether a zone is real is the database's answer, not this
 * function's.
 */
export function isAcceptableTimeZone(value: string | null | undefined): boolean {
  if (!isPlausibleIanaTimeZone(value)) return false;
  const zone = (value ?? '').trim();
  if (zone.startsWith('posix/') || zone.startsWith('right/')) return false;
  return zone.includes('/') || zone === 'UTC';
}

/**
 * The one identifier Crewlink always offers, whether or not the runtime
 * enumerates it. `UTC` is a legitimate, unambiguous choice, and some runtimes
 * omit it from `supportedValuesOf` — leaving a person with no way to pick it.
 */
const ALWAYS_OFFERED = 'UTC';

/**
 * Every IANA zone this runtime knows, for the Settings picker.
 *
 * **THE RUNTIME IS THE LIST, AND CREWLINK MAINTAINS NONE.** `Intl` ships the
 * tzdata; a hand-written list of four hundred place names would be stale within
 * a year and wrong in exactly the way nobody notices until somebody's zone is
 * missing. The database checks existence independently against its OWN tzdata.
 *
 * **THEY ARE SEPARATE AUTHORITIES, AND THEY DO NOT HAVE TO AGREE ON SPELLING.**
 * Two tzdata vintages may offer different valid identifiers for the same zone —
 * `Asia/Calcutta` here, `Asia/Kolkata` there — and **both are correct**. What
 * they share is the acceptance CATEGORY below, not a canonical spelling, and
 * Crewlink never rewrites one into the other.
 *
 * Returns an EMPTY array when the runtime cannot supply one — an older WebView,
 * for instance. **That is a normal state, not an error**, and the picker falls
 * back to typing a zone rather than showing an empty menu.
 *
 * **THE RUNTIME AND THE DATABASE MAY SPELL THE SAME ZONE DIFFERENTLY, AND BOTH
 * SPELLINGS ARE CORRECT** (Jerry, 2026-08-25). IANA keeps LINKS — `Asia/Calcutta`
 * and `Asia/Kolkata`, `Europe/Kiev` and `Europe/Kyiv`, `America/Godthab` and
 * `America/Nuuk` — and different tzdata vintages canonicalise them differently.
 * Crewlink **accepts whichever identifier it is given** and **maintains no
 * rewriting table**: the rules are the same either way, and inventing a
 * preferred spelling would be Crewlink overriding the timezone database.
 *
 * **THE CONSEQUENCE, FOR WHOEVER BUILDS REMINDERS (M12):** two identifiers that
 * differ as STRINGS may name the SAME zone. Nothing may conclude that a
 * reminder was authored in a different timezone from a string comparison alone
 * — equivalence has to be established from what the zones actually do. **That
 * comparison is not implemented here and is not part of S12-00.**
 *
 * `Intl.supportedValuesOf` is ES2022 and not in this project's TS lib target,
 * hence the narrow local declaration rather than a lib bump for one call.
 */
export function readSupportedTimeZones(): readonly string[] {
  try {
    const intl = Intl as typeof Intl & {
      supportedValuesOf?: (key: string) => string[];
    };
    if (typeof intl.supportedValuesOf !== 'function') return [];
    const zones = intl.supportedValuesOf('timeZone');
    if (!Array.isArray(zones)) return [];

    // **THE SAME ACCEPTANCE CATEGORY THE SERVER APPLIES**, so the picker never
    // offers a value Crewlink already knows would be refused.
    const offered = zones.filter(
      (zone): zone is string => typeof zone === 'string' && isAcceptableTimeZone(zone),
    );
    if (offered.length === 0) return [];

    // **`UTC` IS ALWAYS OFFERED, AND NEVER TWICE.** Some runtimes omit it.
    return offered.includes(ALWAYS_OFFERED) ? offered : [ALWAYS_OFFERED, ...offered];
  } catch {
    return [];
  }
}

/**
 * A human-readable description of a zone — **beside the identifier, never
 * instead of it.**
 *
 * `America/New_York` is what Crewlink stores and what this returns alongside;
 * *"Eastern Daylight Time"* is what a person recognises. **The abbreviation is
 * never stored** (S12-00): it is ambiguous across the world and it changes
 * twice a year, which is precisely why the canonical value is the IANA name.
 *
 * Returns null when the runtime cannot describe the zone, so the caller shows
 * the identifier alone rather than an invented label.
 */
export function describeTimeZone(zone: string, at: Date = new Date()): string | null {
  if (!isPlausibleIanaTimeZone(zone)) return null;
  try {
    const parts = new Intl.DateTimeFormat(undefined, {
      timeZone: zone,
      timeZoneName: 'long',
    }).formatToParts(at);
    const name = parts.find((part) => part.type === 'timeZoneName')?.value ?? '';
    return name.trim() === '' ? null : name.trim();
  } catch {
    return null;
  }
}

/**
 * The current UTC offset of a zone, as `GMT-4` / `GMT+8`, or null.
 *
 * Shown as orientation only. **Crewlink stores no offset**: an offset is a
 * property of an instant, not of a place, and storing one would be wrong twice
 * a year in every zone that observes daylight saving.
 */
export function describeTimeZoneOffset(zone: string, at: Date = new Date()): string | null {
  if (!isPlausibleIanaTimeZone(zone)) return null;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      timeZoneName: 'shortOffset',
    }).formatToParts(at);
    const offset = parts.find((part) => part.type === 'timeZoneName')?.value ?? '';
    return offset.trim() === '' ? null : offset.trim();
  } catch {
    return null;
  }
}
